import React, { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import Popover, { PopoverProps } from "antd/es/popover";
import { parse } from "date-fns";
import { useIntl } from "react-intl";

import EmailSchedule from "@mapmycustomers/shared/types/email/EmailSchedule";

import { getEmailExcludedDayIndexes, getEmailQuota, getEmailSendingWindow } from "@app/store/email";
import { RootState } from "@app/store/rootReducer";
import { getLocalTimeZoneDisplayName } from "@app/util/dates";
import { formatDate } from "@app/util/formatters";
import { parseApiDate } from "@app/util/parsers";

import CustomDateView from "./components/CustomDateView";
import Footer from "./components/Footer";
import Header from "./components/Header";
import PresetsView from "./components/PresetsView";
import styles from "./EmailScheduleSendPopover.module.scss";
import messages from "./messages";
import EmailSendingWindow from "./types/EmailSendingWindow";
import getPresetDates, { PresetOption } from "./utils/getPresetDates";

export interface EmailScheduleSendPopoverProps {
  children: React.ReactNode;
  disabled?: boolean;
  isEditing?: boolean;
  onChange: (schedule: EmailSchedule) => void;
  onClose?: () => void;
  onOpenChange?: (open: boolean) => void;
  placement?: PopoverProps["placement"];
  value?: EmailSchedule;
}

const DEFAULT_SENDING_WINDOW: EmailSendingWindow = { endTime: "00:00", startTime: "00:00" };

const EmailScheduleSendPopover = ({
  children,
  disabled,
  emailQuota,
  excludedDayIndexes,
  isEditing,
  onChange,
  onClose,
  onOpenChange,
  placement = "topRight",
  sendingWindow = DEFAULT_SENDING_WINDOW,
  value,
}: EmailScheduleSendPopoverProps & ReturnType<typeof mapStateToProps>) => {
  const intl = useIntl();
  const timezoneDisplayName = useMemo(
    () => getLocalTimeZoneDisplayName(intl.locale),
    [intl.locale]
  );
  const [open, setOpen] = useState(false);
  const [isCustom, setIsCustom] = useState(false);
  const [selectedDateTime, setSelectedDateTime] = useState<Date | undefined>();
  const [presets, setPresets] = useState<PresetOption[]>([]);

  const handleOpen = useCallback(
    (visible: boolean) => {
      if (disabled) {
        return;
      }
      const updatedPresets = getPresetDates({
        dateFormat: intl.formatMessage(messages.presetDateFormat),
        sendingWindow,
      });
      setOpen(visible);
      onOpenChange?.(visible);
      if (visible && value) {
        setIsCustom(true);
        setPresets(updatedPresets);
        setSelectedDateTime(parseApiDate(value.sendAt));
      } else if (visible) {
        setPresets(updatedPresets);
        setSelectedDateTime(updatedPresets[0]?.dateTime);
      } else {
        setIsCustom(false);
        setSelectedDateTime(undefined);
      }
    },
    [disabled, intl, onOpenChange, sendingWindow, value]
  );

  const handleClose = useCallback(() => {
    setOpen(false);
    onOpenChange?.(false);
    setIsCustom(false);
    setSelectedDateTime(undefined);
    onClose?.();
  }, [onClose, onOpenChange]);

  const handleSchedule = useCallback(() => {
    if (!selectedDateTime) {
      return;
    }
    onChange({ sendAt: selectedDateTime.toISOString() });
    handleClose();
  }, [handleClose, onChange, selectedDateTime]);

  const summary = useMemo(
    () =>
      selectedDateTime
        ? formatDate(selectedDateTime, intl.formatMessage(messages.summaryFormat))
        : undefined,
    [intl, selectedDateTime]
  );

  const sendingWindowLabel = useMemo(() => {
    if (!isCustom) {
      return "";
    }
    const start = formatDate(
      parse(sendingWindow.startTime, "HH:mm", new Date()),
      intl.formatMessage(messages.sendingWindowTimeFormat)
    );
    const end = formatDate(
      parse(sendingWindow.endTime, "HH:mm", new Date()),
      intl.formatMessage(messages.sendingWindowTimeFormat)
    );
    return intl.formatMessage(messages.sendTimeTooltip, {
      end,
      start,
      timezone: timezoneDisplayName,
    });
  }, [intl, isCustom, sendingWindow.endTime, sendingWindow.startTime, timezoneDisplayName]);

  const content = (
    <div className={styles.container}>
      <Header
        isCustom={isCustom}
        onChangeCustom={setIsCustom}
        onClose={handleClose}
        timezoneDisplayName={timezoneDisplayName}
      />
      {!isCustom ? (
        <PresetsView
          onChange={setSelectedDateTime}
          onCustomDateClick={() => setIsCustom(true)}
          presets={presets}
          sendingWindow={sendingWindow}
          value={selectedDateTime}
        />
      ) : (
        <CustomDateView
          emailQuota={emailQuota}
          excludedDayIndexes={excludedDayIndexes}
          onChange={setSelectedDateTime}
          sendingWindow={sendingWindow}
          sendingWindowLabel={sendingWindowLabel}
          value={selectedDateTime}
        />
      )}
      <Footer
        disabled={!selectedDateTime}
        isEditing={isEditing}
        onCancel={handleClose}
        onSchedule={handleSchedule}
        summary={summary}
      />
    </div>
  );

  return (
    <Popover
      content={content}
      onOpenChange={handleOpen}
      open={open}
      overlayClassName={styles.popoverOverlay}
      placement={placement}
      trigger="click"
    >
      {children}
    </Popover>
  );
};

const mapStateToProps = (state: RootState) => ({
  emailQuota: getEmailQuota(state),
  excludedDayIndexes: getEmailExcludedDayIndexes(state),
  sendingWindow: getEmailSendingWindow(state),
});

export default connect(mapStateToProps)(EmailScheduleSendPopover);
